import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cva, VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { DrawerPortal } from './drawer-portal';
import { DrawerOverlay } from './drawer-overlay';
import { DrawerClose } from './drawer-close';

const drawerVariants = cva('rocco-drawer fixed z-modal flex outline-none', {
    variants: {
        variant: {
            default: 'px-0 py-0',
            fullWidth: 'rounded-t-2xl bg-background',
            glassy: 'pb-0 md:pb-6',
        },
        side: {
            bottom: 'inset-x-0 bottom-0 h-auto w-full justify-center',
            right: 'inset-y-0 right-0 h-full w-full justify-end',
        },
    },
    defaultVariants: {
        variant: 'default',
        side: 'bottom',
    },
});

const drawerContentVariants = cva(
    'rocco-drawer-content z-modal gap-4 will-change-transform',
    {
        variants: {
            variant: {
                default: 'w-fit bg-white',
                fullWidth: 'w-full',
                glassy: 'w-fit rounded-t-2xl bg-gray-100/85 shadow-lg backdrop-blur md:rounded-2xl',
            },
            side: {
                bottom: '',
                right: 'h-full w-full max-w-[40rem]',
            },
        },
        defaultVariants: {
            variant: 'glassy',
            side: 'bottom',
        },
    }
);

interface DrawerContentProps
    extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>,
        VariantProps<typeof drawerContentVariants> {
    hideDefaultCloseButton?: boolean;
    noOverlay?: boolean;
    noHandle?: boolean;
    limitMaxHeight?: boolean;
    scrollable?: boolean;
    side?: 'bottom' | 'right';
}

export const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Content>,
    DrawerContentProps
>(
    (
        {
            className,
            children,
            variant,
            side = 'bottom',
            hideDefaultCloseButton = false,
            noOverlay = false,
            noHandle = false,
            limitMaxHeight = false,
            scrollable = false,
            ...props
        },
        ref
    ) => (
        <DrawerPortal>
            {!noOverlay && <DrawerOverlay />}
            <DrawerPrimitive.Content
                ref={ref}
                className={cn(
                    drawerVariants({ variant, side, className }),
                    // Keep max height in sync with mobile header height.
                    // packages/rocco/src/ui/website-header/components/MobileHeader.tsx
                    limitMaxHeight && 'max-h-[calc(100dvh-4.75rem)]'
                )}
                {...props}
            >
                <div
                    className={cn(
                        drawerContentVariants({ variant, side }),
                        scrollable && 'overflow-y-auto'
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {!noHandle && side === 'bottom' && (
                        <div className="absolute inset-x-0 top-3 flex h-1 justify-center">
                            <div className="h-1 w-9.5 rounded-full bg-gray-600/30" />
                        </div>
                    )}
                    {children}
                    {!hideDefaultCloseButton && (
                        <DrawerClose className="absolute right-5 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary md:right-6 md:top-7">
                            <X className="h-6 w-6" />
                            <span className="sr-only">Close</span>
                        </DrawerClose>
                    )}
                </div>
            </DrawerPrimitive.Content>
        </DrawerPortal>
    )
);

DrawerContent.displayName = 'DrawerContent';
